import { EventEmitterStripped, UtilsIdentifier } from "@hotelchamp/common";
import { BaseTheme, cssom, Sheet, Twind, twind, tx, TxFunction } from "@twind/core";

import twindConfig from "../../../twind.config";
import { AppZIndexes } from "../constants/AppZIndexes";
import { AppElementsProviderEventTypes } from "../context/AppElementsProviderEventTypes";
import { configManager } from "./configuration/ConfigManager";
import { log } from "./log";

const win = window as any;
class AppElementsProvider {
    private emitter: EventEmitterStripped;

    private containerId = "hc-ibe-root";

    private ibeId = "hc-ibe";

    private container?: HTMLDivElement;

    private containerObserver?: MutationObserver;

    private sheet?: Sheet<CSSStyleSheet>;

    // Use sheet and config to create an twind instance. `tw` will
    // append the right CSS to our custom stylesheet.
    private tw?: Twind<BaseTheme, CSSStyleSheet>;

    private tx?: TxFunction;

    private isAppElementsIntialized = false;

    private ibeInstanceKey?: string;

    private initRetryCount = 0;

    // First try is count 0. The limit is <=
    private initRetryLimit = 2;

    private registeredStyledContainers: HTMLDivElement[] = [];

    private ibeWidgetState: string | undefined;

    public constructor() {
        this.emitter = new EventEmitterStripped();
    }

    public registerStyledContainer = (styledContainer: HTMLDivElement) => {
        if (!this.registeredStyledContainers.includes(styledContainer)) {
            this.registeredStyledContainers.push(styledContainer);

            this.emitter.emit(AppElementsProviderEventTypes.StyledContainerRegistered, styledContainer);
        }
    };

    public unregisterStyledContainer = (styledContainer: HTMLDivElement) => {
        if (this.registeredStyledContainers.includes(styledContainer)) {
            this.registeredStyledContainers = this.registeredStyledContainers.filter(
                (registeredStyledContainer) => registeredStyledContainer !== styledContainer
            );

            this.emitter.emit(AppElementsProviderEventTypes.StyledContainerUnregistered, styledContainer);
        }
    };

    public getRegisteredStyledContainers = () => [...this.registeredStyledContainers];

    public on(event: AppElementsProviderEventTypes, callback: (props: any) => void): () => void {
        return this.emitter.on(event, callback);
    }

    public setIbeWidgetState = (widgetState: string) => {
        this.ibeWidgetState = widgetState;

        if (this.container && this.ibeWidgetState) {
            this.container?.setAttribute("data-hc-ibe-widget-state", this.ibeWidgetState || "");
        }
    };

    public getIbeInstanceKey = () => this.ibeInstanceKey;

    public getContainerElement = () => document.getElementById(this.containerId) as HTMLDivElement;

    public hasExistingContainerElement = () => !!this.getContainerElement();

    /**
     * Helper to create IBE element
     * @returns
     */
    public createIbeElement = () => {
        const ibeElement = document.createElement("hc-ibe");

        ibeElement.id = this.ibeId;

        return ibeElement;
    };

    public getIbeElement = () => document.getElementById(this.ibeId);

    public hasExistingIbeElement = () => !!this.getIbeElement();

    // eslint-disable-next-line no-underscore-dangle
    public getAppFacade = () => window.__HC__?.ibe;

    public initializeAppElements = () => {
        log.debug("Initializing app elements - retry: ", this.initRetryCount);

        const hadExistingContainerElement = this.hasExistingContainerElement();

        if (this.containerObserver) {
            this.containerObserver.disconnect();
        }

        this.container = this.getContainerElement() ?? this.createContainerElement();

        if (!hadExistingContainerElement) {
            const ibe = this.createIbeElement();

            const stripePaymentMethodsContainer = document.createElement("div");
            stripePaymentMethodsContainer.setAttribute("slot", "stripe-payment-methods");

            const stripeExpressCheckoutContainer = document.createElement("div");
            stripeExpressCheckoutContainer.setAttribute("slot", "stripe-express-checkout");

            ibe.appendChild(stripePaymentMethodsContainer);
            ibe.appendChild(stripeExpressCheckoutContainer);

            this.container?.appendChild(ibe);
        }

        if (!this.container && !configManager.isTestMode()) {
            throw Error(`Could not find container element ${this.containerId}`);
        }

        /**
         * @info - Mutation observer to detect when the container is removed from DOM
         *       - This is used to re-initialize the app elements when the container is removed from DOM
         */
        if (this.initRetryCount <= this.initRetryLimit) {
            this.containerObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.removedNodes.forEach((removedNode) => {
                        if ("id" in removedNode && removedNode.id === this.containerId) {
                            this.emitter.emit(AppElementsProviderEventTypes.ContainerRemoved);

                            this.containerObserver?.disconnect();

                            setTimeout(() => {
                                this.initRetryCount++;

                                this.initializeAppElements();
                            }, 750);

                            // The return statement is in there to stop looping through the
                            // rest of the elements once you handled the deletion of the searched element.
                            return;
                        }
                    });
                });
            });

            this.containerObserver.observe(document.documentElement, { subtree: true, childList: true });
        }

        // attach shadow DOM to container
        // export const shadowRoot = shadowContainer?.attachShadow({ mode: "open" });

        /**
         * Handle Tailwind CSS for shadow DOM
         * @info - https://gourav.io/blog/tailwind-in-shadow-dom
         */

        if (this.initRetryCount === 0) {
            // // Create separate CSSStyleSheet
            const plainSheet = new CSSStyleSheet();

            // Temp solution to inject custom styles to hide scrollbar but to keep scrolling functionality
            // Used for slideshow
            // @todo - find better implementation

            if (!configManager.isTestMode()) {
                // plainSheet.replaceSync(`
                // @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700&display=swap');
                // .no-scrollbar::-webkit-scrollbar {
                //     display: none;
                // }
                // .no-scrollbar {
                //     -ms-overflow-style: none; /* IE and Edge */
                //     scrollbar-width: none; /* Firefox */
                // }`);
            }

            win.plainSheet = plainSheet;

            this.sheet = cssom(plainSheet);

            this.tw = twind(twindConfig, this.sheet);

            this.tx = tx.bind(this.tw);

            this.isAppElementsIntialized = true;
        }
    };

    public getInitRetryCount = () => this.initRetryCount;

    public getInitRetryLimit = () => this.initRetryLimit;

    public getAppElements = () => ({
        container: this.container,
        sheet: this.sheet,
        tw: this.tw,
        tx: this.tx,
        isAppElementsIntialized: this.isAppElementsIntialized,
        ibeInstanceKey: this.ibeInstanceKey,
    });

    protected createContainerElement = () => {
        const containerElement = document.createElement("div");

        this.ibeInstanceKey = UtilsIdentifier.uuid();

        containerElement.id = this.containerId;
        containerElement.style["WebkitOverflowScrolling" as any] = "auto";
        containerElement.style.overflow = "auto";
        containerElement.style.zIndex = AppZIndexes.Min.toString();
        containerElement.style.position = "relative";
        containerElement.style.fontFamily = '"DM Sans", system-ui, sans-serif';
        containerElement.setAttribute("bablic-exclude", "");
        containerElement.setAttribute("data-hc-ibe-instance-key", UtilsIdentifier.uuid());

        document.body.appendChild(containerElement);

        return containerElement;
    };
}

export const appElementsProvider = new AppElementsProvider();
